import React, { useState } from 'react';
import {
  Drawer,
  List,
  ListItemButton,
  ListItemText,
  IconButton,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
} from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { useAnda } from './AndaContext';
import { getChatHistory, deleteChatThread, renameChatThread } from '../../api/andaApi';
import './anda.scss';

const ANDA_GREEN = '#7FBE25';
const ANDA_GREEN_HOVER = '#6AA318';

export default function AndaHistoryMenu() {
  const { historyOpen, closeHistory, historyList, setHistoryList, setMessages, setThreadId, threadId } = useAnda();
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [menuThread, setMenuThread] = useState(null);
  const [renameOpen, setRenameOpen] = useState(false);
  const [renameValue, setRenameValue] = useState('');
  const [renameThread, setRenameThread] = useState(null);

  const projectId = Number(localStorage.getItem('project_id')) || null;
  const acctNo = Number(localStorage.getItem('user_id')) || null;

  const refreshHistory = () => {
    if (projectId && acctNo) {
      getChatHistory({ acct_no: acctNo, project_id: projectId })
        .then(setHistoryList)
        .catch(() => setHistoryList([]));
    }
  };

  const handleSelectThread = (thread) => {
    setMessages(thread.messages || []);
    setThreadId(thread.thread_id);
    closeHistory();
  };

  const handleMenuOpen = (e, thread) => {
    e.stopPropagation();
    setMenuAnchor(e.currentTarget);
    setMenuThread(thread);
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
    setMenuThread(null);
  };

  const handleDelete = async () => {
    if (!menuThread || !acctNo || !projectId) return;
    try {
      await deleteChatThread({
        acct_no: acctNo,
        project_id: projectId,
        thread_id: menuThread.thread_id,
      });
      if (menuThread.thread_id === threadId) {
        setMessages([]);
        setThreadId(null);
      }
      refreshHistory();
    } catch (err) {
      console.error('Delete chat failed:', err);
    }
    handleMenuClose();
  };

  const handleRenameClick = () => {
    if (menuThread) {
      setRenameThread(menuThread);
      setRenameValue(menuThread.chat_title || '');
      setRenameOpen(true);
    }
    handleMenuClose();
  };

  const handleRenameSubmit = async () => {
    if (!renameThread || !acctNo || !projectId) return;
    try {
      await renameChatThread({
        acct_no: acctNo,
        project_id: projectId,
        thread_id: renameThread.thread_id,
        chat_title: renameValue.trim() || '新對話',
      });
      refreshHistory();
    } catch (err) {
      console.error('Rename chat failed:', err);
    }
    setRenameOpen(false);
    setRenameThread(null);
    setRenameValue('');
  };

  const handleRenameClose = () => {
    setRenameOpen(false);
    setRenameThread(null);
    setRenameValue('');
  };

  return (
    <>
      <Drawer
        anchor="left"
        open={historyOpen}
        onClose={closeHistory}
        variant="temporary"
        ModalProps={{ disableScrollLock: true }}
        sx={{
          '& .MuiDrawer-paper': {
            width: 240,
            top: 50,
            height: 'calc(100% - 50px)',
          },
        }}
        className="anda-history-drawer"
      >
        <div className="anda-history-header">歷史對話</div>
        {historyList.length === 0 ? (
          <div className="anda-history-empty">尚無歷史對話</div>
        ) : (
          <List>
            {historyList.map((thread) => (
              <ListItemButton
                key={thread.thread_id}
                onClick={() => handleSelectThread(thread)}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  pr: 0.5,
                  '& .MuiIconButton-root': { opacity: 0 },
                  '&:hover .MuiIconButton-root': { opacity: 1 },
                }}
              >
                <ListItemText
                  primary={thread.chat_title || '新對話'}
                  primaryTypographyProps={{ noWrap: true }}
                  sx={{ flex: 1, minWidth: 0 }}
                />
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleMenuOpen(e, thread);
                  }}
                  aria-label="更多選項"
                  sx={{ color: '#666', flexShrink: 0, transition: 'opacity 0.2s' }}
                >
                  <MoreVertIcon fontSize="small" />
                </IconButton>
              </ListItemButton>
            ))}
          </List>
        )}
      </Drawer>

      <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={handleMenuClose} onClick={(e) => e.stopPropagation()}>
        <MenuItem onClick={handleRenameClick}>重新命名</MenuItem>
        <MenuItem onClick={handleDelete} sx={{ color: 'error.main' }}>
          刪除
        </MenuItem>
      </Menu>

      <Dialog
        open={renameOpen}
        onClose={handleRenameClose}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            boxShadow: '0 4px 20px rgba(0,0,0,0.12)',
          },
        }}
      >
        <DialogTitle sx={{ fontSize: 18, fontWeight: 600, color: '#323232', borderBottom: '1px solid #e0e0e0', pb: 2 }}>
          重新命名對話
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <TextField
            autoFocus
            margin="dense"
            label="對話標題"
            fullWidth
            variant="outlined"
            value={renameValue}
            onChange={(e) => setRenameValue(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleRenameSubmit()}
            sx={{
              '& .MuiOutlinedInput-root.Mui-focused fieldset': { borderColor: ANDA_GREEN },
              '& .MuiInputLabel-root.Mui-focused': { color: ANDA_GREEN },
            }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, pt: 0, gap: 1 }}>
          <Button onClick={handleRenameClose} sx={{ color: '#666' }}>
            取消
          </Button>
          <Button
            onClick={handleRenameSubmit}
            variant="contained"
            sx={{
              backgroundColor: ANDA_GREEN,
              '&:hover': { backgroundColor: ANDA_GREEN_HOVER },
            }}
          >
            確定
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
